import React, { useState, useCallback, useEffect, useRef } from "react";
import FormCard from "../FormComponents/FormCard";
import AlertBanner from "../FormComponents/AlertBanner";
import ExhibitorInfoBar from "../FormComponents/ExhibitorInfoBar";
import Icon from "../Icons/Icon";

/**
 * Step 6: New Products & Power Alley
 *
 * Backend keys:
 *
 * newProductsParticipate  String  "yes"|"no"  required
 *   If "yes", at least one product row is required:
 *     product1ItemNumber  String  optional
 *     product1Desc        String  optional
 *     product2ItemNumber  String  optional
 *     product2Desc        String  optional
 *     product3ItemNumber  String  optional
 *     product3Desc        String  optional
 *     product4ItemNumber  String  optional
 *     product4Desc        String  optional
 *     product5ItemNumber  String  optional
 *     product5Desc        String  optional
 *   Error key if no products entered: errors.products
 *
 * powerAlleyParticipate   String  "yes"|"no"  required
 *   Error key if not selected: errors.powerAlleyParticipate
 *   If "yes", item table fields apply (3 columns):
 *     powerAlley1ItemNumber  String  optional
 *     powerAlley1Desc        String  optional
 *     powerAlley1MinBuy      String  optional
 *     powerAlley1NetCost     String  optional
 *     powerAlley1Discount    String  optional
 *     powerAlley2ItemNumber … powerAlley2Discount  (same pattern)
 *     powerAlley3ItemNumber … powerAlley3Discount  (same pattern)
 */
const Step6 = ({
  exhibitorData,
  initialContract,
  onSave,
  onDataChange,
  disabled = false,
  errors = {},
}) => {
  const [formData, setFormData] = useState({
    newProductsParticipate: initialContract?.newProductsParticipate || "",
    product1ItemNumber: initialContract?.product1ItemNumber || "",
    product1Desc: initialContract?.product1Desc || "",
    product2ItemNumber: initialContract?.product2ItemNumber || "",
    product2Desc: initialContract?.product2Desc || "",
    product3ItemNumber: initialContract?.product3ItemNumber || "",
    product3Desc: initialContract?.product3Desc || "",
    product4ItemNumber: initialContract?.product4ItemNumber || "",
    product4Desc: initialContract?.product4Desc || "",
    product5ItemNumber: initialContract?.product5ItemNumber || "",
    product5Desc: initialContract?.product5Desc || "",
    powerAlleyParticipate: initialContract?.powerAlleyParticipate || "",
    ...[1, 2, 3].reduce(
      (acc, n) => ({
        ...acc,
        [`powerAlley${n}ItemNumber`]:
          initialContract?.[`powerAlley${n}ItemNumber`] || "",
        [`powerAlley${n}Desc`]: initialContract?.[`powerAlley${n}Desc`] || "",
        [`powerAlley${n}MinBuy`]:
          initialContract?.[`powerAlley${n}MinBuy`] || "",
        [`powerAlley${n}NetCost`]:
          initialContract?.[`powerAlley${n}NetCost`] || "",
        [`powerAlley${n}Discount`]:
          initialContract?.[`powerAlley${n}Discount`] || "",
      }),
      {},
    ),
  });

  const formDataRef = useRef(formData);
  const autoSaveTimerRef = useRef(null);
  const hasChangedRef = useRef(false);

  // Keep ref in sync
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  // Notify parent of data changes
  useEffect(() => {
    if (onDataChange) onDataChange(formData);
  }, [formData, onDataChange]);

  // Auto-save with debounce
  useEffect(() => {
    if (!hasChangedRef.current || disabled) return;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      if (onSave) onSave(formDataRef.current);
    }, 3000);
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [formData, onSave, disabled]);

  // Save on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
      if (hasChangedRef.current && onSave) {
        onSave(formDataRef.current);
      }
    };
  }, [onSave]);

  const updateField = useCallback((field, value) => {
    hasChangedRef.current = true;
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const hasErrors = Object.keys(errors).length > 0;

  return (
    <fieldset
      disabled={disabled}
      style={{ border: "none", padding: 0, margin: 0 }}
    >
      <div className="form-page">
        <div className="form-page-header">
          <h2 className="form-page-title">New Products & Power Alley</h2>
        </div>

        {hasErrors && (
          <AlertBanner type="warning">
            <strong>Please fix the following errors:</strong>{" "}
            {Object.values(errors).join(", ")}
          </AlertBanner>
        )}

        <ExhibitorInfoBar exhibitorData={exhibitorData} />

        {/* ── New Products ───────────────────────────────────────── */}
        <FormCard title="New Products" icon={<Icon name="star" size={14} />}>
          <p
            style={{
              fontSize: 14,
              color: "var(--neutral-700)",
              lineHeight: 1.7,
              marginBottom: 14,
            }}
          >
            The "NEW" Lawn and Garden Products for the upcoming season are
            introduced at the Arett Open House. New Products are the driving
            force of each show and represent a large amount of new business each
            year.
          </p>
          <AlertBanner type="info">
            INSTRUCTIONS: PLEASE FILL OUT ALL FIELDS – ENTER N/A WHERE NOT
            APPLICABLE
          </AlertBanner>
          <AlertBanner type="info">
            <strong>
              Samples of all New Products (required) should be delivered to the
              New Product area by Tuesday of show week by Noon (12:00PM). $175
              for every 5 items.
            </strong>{" "}
            All new items must be included in the Arett Item Setup Form.
          </AlertBanner>

          <div
            className="radio-group"
            style={{
              marginTop: 16,
              marginBottom: errors.newProductsParticipate ? 4 : 16,
            }}
          >
            <label className="custom-radio">
              <input
                type="radio"
                name="newProductsParticipate"
                value="yes"
                checked={formData.newProductsParticipate === "yes"}
                onChange={() => updateField("newProductsParticipate", "yes")}
              />
              <span>Yes, I opt to participate</span>
            </label>
            <label className="custom-radio">
              <input
                type="radio"
                name="newProductsParticipate"
                value="no"
                checked={formData.newProductsParticipate === "no"}
                onChange={() => {
                  updateField("newProductsParticipate", "no");
                }}
              />
              <span>No, I opt NOT to participate</span>
            </label>
          </div>
          {errors.newProductsParticipate && (
            <span
              className="field-error"
              style={{ display: "block", marginBottom: 16 }}
            >
              {errors.newProductsParticipate}
            </span>
          )}

          {formData.newProductsParticipate === "yes" && (
            <>
              {errors.products && (
                <span
                  className="field-error"
                  style={{ display: "block", marginBottom: 8 }}
                >
                  {errors.products}
                </span>
              )}
              <table className="contract-table">
                <thead>
                  <tr>
                    <th style={{ width: 50 }}>#</th>
                    <th>Arett Item Number</th>
                    <th>Product Description</th>
                  </tr>
                </thead>
                <tbody>
                  {[1, 2, 3, 4, 5].map((num) => (
                    <tr key={num}>
                      <td>{num}.</td>
                      <td>
                        <input
                          className="form-input"
                          value={formData[`product${num}ItemNumber`]}
                          onChange={(e) =>
                            updateField(
                              `product${num}ItemNumber`,
                              e.target.value,
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="form-input"
                          value={formData[`product${num}Desc`]}
                          onChange={(e) =>
                            updateField(`product${num}Desc`, e.target.value)
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </FormCard>

        {/* ── Power Alley ────────────────────────────────────────── */}
        <FormCard title="Power Alley" icon={<Icon name="star" size={14} />}>
          <AlertBanner type="info" style={{ marginBottom: 16 }}>
            INSTRUCTIONS: PLEASE FILL OUT ALL FIELDS – ENTER N/A WHERE NOT
            APPLICABLE
          </AlertBanner>

          <div className="alert-banner info" style={{ marginBottom: 16 }}>
            <div>
              <strong>PARTICIPATE IN THE POWER OF POWER ALLEY!</strong>
              <br />
              An extra discount incentive is a major reason to come to a show
              and write orders. Our Power Alley Booth that features your
              displays and pallet programs is a tremendously successful
              destination booth at the LIVE show and has proven to be
              tremendously successful at the Virtual Show as well.
            </div>
          </div>

          <div
            style={{
              fontWeight: 700,
              fontSize: 14,
              marginBottom: 10,
              color: "var(--neutral-800)",
            }}
          >
            Parameters for participation:
          </div>
          <p
            style={{
              fontSize: 13,
              color: "var(--neutral-600)",
              marginBottom: 4,
            }}
          >
            A Display sample is important to drive sales. If no sample is
            available, a banner can stand in for the product. Samples should be
            shipped with booth samples and labeled with "Power Alley" label
            found in the General Exposition Services Packet.
          </p>
          <ul
            style={{
              fontSize: 13,
              paddingLeft: 18,
              lineHeight: 2,
              color: "var(--neutral-600)",
              marginBottom: 16,
            }}
          >
            <li>$375 per item</li>
            <li>Minimum dealer value of $200 – after all discounts</li>
            <li>Minimum Arett savings above Early Order discount of 15%</li>
          </ul>

          <div
            className="radio-group"
            style={{ marginBottom: errors.powerAlleyParticipate ? 4 : 0 }}
          >
            <label className="custom-radio">
              <input
                type="radio"
                name="powerAlleyParticipate"
                value="yes"
                checked={formData.powerAlleyParticipate === "yes"}
                onChange={() => updateField("powerAlleyParticipate", "yes")}
              />
              <span>Yes, I opt to participate</span>
            </label>
            <label className="custom-radio">
              <input
                type="radio"
                name="powerAlleyParticipate"
                value="no"
                checked={formData.powerAlleyParticipate === "no"}
                onChange={() => updateField("powerAlleyParticipate", "no")}
              />
              <span>No, I opt NOT to participate</span>
            </label>
          </div>
          {errors.powerAlleyParticipate && (
            <span
              className="field-error"
              style={{ display: "block", marginTop: 4 }}
            >
              {errors.powerAlleyParticipate}
            </span>
          )}

          {formData.powerAlleyParticipate === "yes" && (
            <div style={{ overflowX: "auto", marginTop: 16 }}>
              <table className="contract-table">
                <thead>
                  <tr>
                    <th style={{ minWidth: 160 }}></th>
                    <th>1st Item</th>
                    <th>2nd Item</th>
                    <th>3rd Item</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { label: "Arett Item #:", field: "ItemNumber" },
                    { label: "Item Desc:", field: "Desc" },
                    { label: "Min. Buy:", field: "MinBuy" },
                    { label: "Arett Net Power Alley Cost:", field: "NetCost" },
                    { label: "Discount %:", field: "Discount" },
                  ].map(({ label, field }) => (
                    <tr key={field}>
                      <td style={{ fontWeight: 600, fontSize: 13 }}>{label}</td>
                      {[1, 2, 3].map((n) => (
                        <td key={n}>
                          <input
                            className="form-input"
                            value={formData[`powerAlley${n}${field}`]}
                            onChange={(e) =>
                              updateField(
                                `powerAlley${n}${field}`,
                                e.target.value,
                              )
                            }
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </FormCard>
      </div>
    </fieldset>
  );
};

export default Step6;
